import React, { useContext, useState, useCallback } from 'react';
import {
  Drawer,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import useSessionStore from '../../../../../hooks/useSessionStore';
import feedType from '../../../../context/FeedContext/config/feedTypes';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import FeedContext from '../../../../context/FeedContext';
import { STORAGE_KEYS } from '../../../../layout/context/LayoutContext';
import captureStorageError from '../../../../../utils/localStorage';
import FeedSearch from '../FeedSearch';

import useFeedListConfig from './useFeedListConfig';
import {
  getDrawerStyle,
  FeedListFooter,
  StyledBadge,
  StyledList,
  FeedListContainer,
  FilterContainer,
  FilterBar,
  SortContainer,
  StyledSortIcon,
  ToggleButton,
  CollapsedBadge,
  StyledListItem,
  StyledListItemText,
} from './styles';
import texts from './texts.json';

const getStoredCollapsedState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.FEED_LIST_COLLAPSED);
    if (stored !== null) {
      return stored === 'true';
    }
  } catch (e) {
    captureStorageError('getItem', STORAGE_KEYS.FEED_LIST_COLLAPSED, e, 'ActivityFeed FeedList');
  }
  return true; // default to collapsed
};

const FeedList = () => {
  const { isCoachAssistant } = useSessionStore();
  const [isCollapsed, setIsCollapsedState] = useState(getStoredCollapsedState);
  const {
    selectedFeedType,
    setIsOldestToNewest,
    isOldestToNewest,
    showAutomatedItems,
    setShowAutomatedItems,
  } = useContext(FeedContext);
  const { feedListEntries, footerFeedListEntries } = useFeedListConfig();

  const showAutomatedFilter = selectedFeedType === feedType.NEED_NEW_MEAL_PLAN
    || selectedFeedType === feedType.NEED_NEW_WORKOUTS;
  const showFilterBar = isCoachAssistant;

  const toggleCollapse = useCallback(() => {
    setIsCollapsedState((prev) => {
      const newValue = !prev;
      try {
        localStorage.setItem(STORAGE_KEYS.FEED_LIST_COLLAPSED, String(newValue));
      } catch (e) {
        captureStorageError('setItem', STORAGE_KEYS.FEED_LIST_COLLAPSED, e, 'ActivityFeed FeedList');
      }
      return newValue;
    });
  }, []);

  return (
    <FeedListContainer $isCollapsed={isCollapsed}>
      {isCollapsed ? (
        <ToggleButton onClick={toggleCollapse} $isCollapsed $size="small">
          <ChevronRightIcon />
        </ToggleButton>
      ) : (
        <FilterContainer>
          <FeedSearch />
          <SortContainer onClick={() => setIsOldestToNewest((prev) => !prev)}>
            <Tooltip
              title={isOldestToNewest ? texts.newestToOldest : texts.oldestToNewest}
              placement="top"
              arrow
            >
              <StyledSortIcon />
            </Tooltip>
          </SortContainer>
          <ToggleButton onClick={toggleCollapse} $size="small">
            <ChevronLeftIcon />
          </ToggleButton>
        </FilterContainer>
      )}
      {showFilterBar && (
        // We always show the filter bar for the AC to keep the drawer height consistent
        <FilterBar>
          {!isCollapsed && showAutomatedFilter && (
            <LabelCheckbox
              isChecked={showAutomatedItems}
              onChange={setShowAutomatedItems}
              description={texts.showAutomatedItems}
            />
          )}
        </FilterBar>
      )}
      <Drawer
        variant="permanent"
        open
        sx={getDrawerStyle(isCollapsed, showFilterBar)}
      >
        <StyledList>
          {feedListEntries.map(({
            title,
            icon: IconComponent,
            onClick,
            feedId,
            unreadCount,
          }) => (
            <Tooltip
              key={feedId}
              title={isCollapsed ? title : ''}
              placement="right"
              arrow
            >
              <StyledListItem
                button
                onClick={onClick}
                selected={selectedFeedType === feedId}
                $isCollapsed={isCollapsed}
              >
                <ListItemIcon>
                  <IconComponent />
                </ListItemIcon>
                <StyledListItemText
                  primary={title}
                  $isCollapsed={isCollapsed}
                  aria-hidden={isCollapsed}
                />
                {isCollapsed ? (
                  unreadCount > 0 && <CollapsedBadge>{unreadCount}</CollapsedBadge>
                ) : (
                  <StyledBadge
                    badgeContent={unreadCount}
                    showZero
                    max={999}
                  />
                )}
              </StyledListItem>
            </Tooltip>
          ))}
        </StyledList>
        <FeedListFooter>
          {footerFeedListEntries.map(({
            title,
            icon: IconComponent,
            onClick,
            feedId,
          }) => (
            <Tooltip
              key={feedId}
              title={isCollapsed ? title : ''}
              placement="right"
              arrow
            >
              <StyledListItem
                button
                onClick={onClick}
                selected={selectedFeedType === feedId}
                $isCollapsed={isCollapsed}
              >
                <ListItemIcon>
                  <IconComponent />
                </ListItemIcon>
                <StyledListItemText
                  primary={title}
                  $isCollapsed={isCollapsed}
                  aria-hidden={isCollapsed}
                />
              </StyledListItem>
            </Tooltip>
          ))}
        </FeedListFooter>
      </Drawer>
    </FeedListContainer>
  );
};

export default FeedList;
